import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { InvAttachedItem, InvFileAttachment, InvTraveler } from 'src/models';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AimsCommonConfirmDialogComponent, AimsCommonPopupEditorComponent, AimsCommonTableHeader, AimsCommonTablePermissions, TableRowUpdate } from '@aims/common-components';
import { DialogComponent } from 'src/app/services/message/message.enum';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'src/app/services/message/message.service';
import { InventoryReturnPopupComponent } from '../../assetMgmt/workorders/workOrder/inventory-return-popup/inventory-return-popup.component';

@Component({
  selector: 'app-traveler',
  templateUrl: './traveler.component.html',
  styleUrls: ['./traveler.component.css']
})
export class TravelerComponent implements OnInit {

  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = false;

  // AimsCommonTable setup
  header: AimsCommonTableHeader[] = [
    { key: 'rowExpand', label: '', type: 'rowExpand', filter: false },
    { key: 'return', label: 'Return', type: 'button',
            events: { click: (element) => this.openReturnPopup(element) }, filter: false },
    { key: 'inventoryItem.barcode', label: 'Barcode', type: 'number', filter: false, width: 100, },
    { key: 'inventoryItem.description', label: 'Description', type: 'string', filter: false, width: 300, },
    { key: 'quantity', label: 'Quantity', type: 'dynamic', filter: false, width: 120 },
    { key: 'inventoryItem.unitOfIssueId', label: 'Unit of Issue', type: 'string', filter: false, width: 80, },
    { key: 'partCost', label: 'Part Cost', type: 'dynamic', filter: false, },
  ];
  subTableHeader: AimsCommonTableHeader[] = [
    { key: 'quantity', label: 'Quantity', type: 'number', filter: false },
    { key: 'pulledDate', label: 'Date', type: 'date', filter: false },
    { key: 'pulledByEmployeeId', label: 'User', type: 'dynamic', editable: false },
    { key: 'unitCost', label: 'Unit Cost', type: 'dynamic', filter: false },
  ];
  tablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: false,
    delete: false,
  };

  //Header
  headerText = "Traveler";

  destroy$: Subject<boolean> = new Subject();

  //Form Control
  newItem: boolean;
  traveler: InvTraveler = new InvTraveler();

  documents: InvFileAttachment[] = [];

  attachedItems: InvAttachedItem [] = [];
  employeeValues = this.transferHttp.getCache('employeeDropdown');
  travelerStatuses = this.transferHttp.getSingle('Inventory/GetInvTravelerStatuses');

  constructor(private transferHttp: TransferHttpService,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private messageService: MessageService,) {
  }

  ngOnDestroy() {
      this.destroy$.next(true);
      this.destroy$.unsubscribe();
  }

  ngOnInit(): void {
    window.scrollTo(0,0);

    //If id provided, load existing req data
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(p => {
      if (p['wiId']) {
        this.loadTraveler(p['wiId']);
        this.showSpinner = false;
      }
      else {
        this.showSpinner = false;
        this.initializeForm();
      }
    });
  }

  initializeForm() : void {
    this.newItem = true;
    this.traveler = new InvTraveler();
    this.traveler.status = 'BLD';
  }

  public saveTraveler(traveler : InvTraveler): void {
    this.showSpinner = true;
    this.spinnerText = "Saving Traveler...";

    if (!this.newItem) {
      this.transferHttp.put('inventory/SaveTraveler', traveler).subscribe(retVal => {
        this.showSpinner = false;
      });
    }
    else {
      this.transferHttp.post('inventory/CreateTraveler', traveler).subscribe(retVal => {
        this.newItem = false;
        this.showSpinner = false;
        this.loadTraveler(retVal);
      });
    }
  }

  public loadTraveler(travelerId: number): void {
      this.spinnerText = "Loading Traveler...";
      this.showSpinner = true;
      this.documents.length = 0;
      this.transferHttp.get(`inventory/GetTraveler?travelerId=${travelerId}`).subscribe(traveler => {
        if (traveler) {
          this.traveler = traveler;
          this.newItem = false;
          this.loadTravelerDocuments(this.traveler.travelerId);
          this.loadAttachedItems(this.traveler.travelerId);
        }
        else {
          this.newItem = true;
        }
        this.showSpinner = false;
      });
  }

  public loadTravelerDocuments(travelerId: number): void {
    this.transferHttp.get(`inventory/GetTravelerDocuments?travelerId=${travelerId}` ).subscribe(docs => {
      this.documents = docs;
    });
  }

  public addItemToTraveler(event) {
      this.showSpinner = true;
      var url = `inventory/AddPartToTraveler?travelerId=${this.traveler.travelerId}&inventoryItemId=${event.inventoryItemId}&quantity=${event.quantity}`;
      this.transferHttp.put(url, null)
        .subscribe(retVal => {
            this.loadAttachedItems(this.traveler.travelerId);
      });
  }

  public showAddPart(): boolean {
    return !this.newItem;
  }

  public cancelTraveler() {
    var message = this.messageService.getDialogMessage(DialogComponent.UnsavedChanges);
    let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent,{
        data: message
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if(dialogResult){
        this.router.navigate([`/inventory/viewTravelers`]);
      }
      dialogRef = null;
    });
  }

  public calculateEditable() : boolean {
    return this.newItem;
  }

  public uploadEditable() : boolean {
    return !this.newItem && this.traveler.travelerId != 0 ;
  }

  public loadAttachedItems(travelerId: number) {
    if (travelerId == 0) {
      return;
    }

    this.showSpinner = true;
    this.transferHttp.get(`inventory/GetTravelerItems?travelerId=${travelerId}` ).subscribe(retval => {
          this.attachedItems = retval;
          this.showSpinner = false;
        });
  }

  public getAttachedItemPulledQuantity(attachedItemId: number): number {
    const sum: number = this.attachedItems.filter(wi => wi.attachedItemId === attachedItemId)[0]
      .invAttachedItemPulled
      .map(i => i.quantity)
      .reduce((sum, current) => sum + current, 0);
    return sum;
  }

 public openReturnPopup(itemToReturn: InvAttachedItem) {
    const originalQuantity = this.getAttachedItemPulledQuantity(itemToReturn.attachedItemId);

    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            data: {
                btnOkText: 'Return',
                header: 'Return Quantity',
                content: InventoryReturnPopupComponent,
                contentData: { itemToReturn: itemToReturn, maxReturnableQuantity: originalQuantity },
            },
            disableClose: true
        });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.returnInventoryItemQuantity(itemToReturn.attachedItemId, dialogResult.returnedQuantity);
      }
    });
  }

  public returnInventoryItemQuantity(attachedItemId: number, returnedQuantity: number) {
    this.spinnerText = "Returning Inventory Item...";
    this.showSpinner = true;

    var url = `inventory/RemoveQuantityFromAttachedItem?attachedItemId=${attachedItemId}&quantityToRemove=${returnedQuantity}`;
    this.transferHttp.put(url, null)
      .subscribe({
        next: _ => {
          this.loadAttachedItems(this.traveler.travelerId);
          this.showSpinner = false;
        },
        error: err => {
          this.showSpinner = false;
        }
      });
  }

  public getAttachedItemPulledCost(attachedItemId: number): number {
    if (attachedItemId == 0) {
      return 0;
    }
    const totalCost: number = this.attachedItems.filter(wi => wi.attachedItemId === attachedItemId)[0].invAttachedItemPulled
      .map(i => i.quantity *  i.inventoryItemReceived.costPerUnit)
      .reduce((sum, current) => sum + current, 0);
    return totalCost;
  }

  public getTotalAttachedItemsPulledCost(): number {
    var totalCost: number = 0;
      this.attachedItems.forEach(iwi => {
        if (iwi.invAttachedItemPulled[0].attachedItemPulledId == 0) {
          totalCost += 0;
        }
        else {
        totalCost += iwi.invAttachedItemPulled
            .map(i => i.quantity *  i.inventoryItemReceived.costPerUnit)
            .reduce((sum, current) => sum + current, 0);
        }
      })
    return totalCost;
  }

}
